import React from "react";
import {observer} from "mobx-react-lite";
import {Box, InputAdornment, TextField, Typography} from "@mui/material";
import {SearchOutlined} from "@mui/icons-material";
import type {GridColDef} from "@mui/x-data-grid-premium";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid.tsx";
import type {KpiBoxedValue} from "../../../../components/kpi-boxed-values/KpiBoxedValues.tsx";
import {KpiBoxedValues} from "../../../../components/kpi-boxed-values/KpiBoxedValues.tsx";
import {buildTermLookup, termDaysLabel} from "../paymentTermsUtils.ts";
import {
    formatTableMoney,
    MISSING,
    moneyOrMissing,
    pctOrMissing,
    targetDaysPhrase,
} from "../paymentTermsDisplay.ts";
import {usePaymentTermsMappingQuery, usePaymentTermsPageDisplay} from "../usePaymentTermsQueries.ts";
import {TermGaugeCell} from "./PaymentTermsCells.tsx";
import type {PtSupplier} from "~/experiments/pages/payment-terms/PaymentTermsClasses.ts";

type Props = {
    rows: PtSupplier[];
    onSelect: (row: PtSupplier) => void;
};

/**
 * Internal payment-term disparity: the same supplier paid on different terms
 * across entities or business units. Harmonising to the single best term is
 * the lowest-friction working-capital lever.
 */
export const HarmonisationView: React.FC<Props> = observer(({rows, onSelect}) => {
    const d = usePaymentTermsPageDisplay();
    const currency = d.currency;
    const [search, setSearch] = React.useState('');
    const {data: mapping = []} = usePaymentTermsMappingQuery();
    const termLookup = React.useMemo(() => buildTermLookup(mapping), [mapping]);

    const multiTerm = React.useMemo(() =>
        rows.filter(r => r.multipleTerms)
            .sort((a, b) => b.quickWinOpportunity - a.quickWinOpportunity || b.distinctTermCount - a.distinctTermCount),
    [rows]);

    const filtered = React.useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return multiTerm;
        return multiTerm.filter(r =>
            r.supplier.toLowerCase().includes(q) ||
            r.terms.some(t => t.toLowerCase().includes(q)));
    }, [multiTerm, search]);

    const kpis: KpiBoxedValue[] = [
        {
            key: 'count',
            title: 'Suppliers on multiple terms',
            subtitle: d.suppliersWithMultipleTerms == null
                ? MISSING
                : d.suppliersWithMultipleTerms.toLocaleString('en-US'),
            help: 'Supplier parents paid on more than one payment term across entities or business units — ' +
                'a sign of fragmented negotiation, not supplier resistance.',
        },
        {
            key: 'spend',
            title: 'Spend on multi-term suppliers',
            subtitle: moneyOrMissing(d.multiTermSpend, currency),
            help: 'Total 12-month spend with suppliers that have more than one payment term in use.',
        },
        {
            key: 'quickwin',
            title: 'Harmonisation quick win',
            subtitle: moneyOrMissing(d.multiTermQuickWin, currency),
            help: 'Cash released by aligning every entity to the best term each supplier already accepts — ' +
                'no new negotiation leverage needed.',
        },
        {
            key: 'policy',
            title: 'Suppliers meeting policy',
            subtitle: pctOrMissing(d.suppliersMeetingPolicyPct),
            help: `Share of suppliers whose weighted term meets the ${targetDaysPhrase(d.targetTermDays)} policy today.`,
        },
    ];

    const columns = React.useMemo<GridColDef<PtSupplier>[]>(() => [
        {
            field: 'supplier',
            headerName: 'Parent supplier',
            flex: 1.5,
            minWidth: 220,
            renderCell: ({row}) => <span className="pt-supplier-name">{row.supplier}</span>,
        },
        {
            field: 'distinctTermCount',
            headerName: 'Terms in use',
            type: 'number',
            flex: 0.55,
            minWidth: 105,
        },
        {
            field: 'terms',
            headerName: 'Payment terms',
            flex: 2,
            minWidth: 300,
            sortable: false,
            renderCell: ({row}) => (
                <Box className="pt-harm-chips">
                    {row.terms.slice(0, 3).map(t => {
                        const days = termDaysLabel(t, termLookup);
                        return (
                            <span key={t} className="pt-term-chip">
                                {t}{days && <span className="pt-term-chip-days">{days}</span>}
                            </span>
                        );
                    })}
                    {row.distinctTermCount > 3 &&
                        <span className="pt-term-chip pt-term-chip-more">+{row.distinctTermCount - 3}</span>}
                </Box>
            ),
        },
        {
            field: 'spend',
            headerName: 'Total spend (period)',
            type: 'number',
            flex: 0.75,
            minWidth: 135,
            valueFormatter: (value: number) => formatTableMoney(value, currency),
        },
        {
            field: 'currentTerm',
            headerName: 'Weighted avg term vs target',
            type: 'number',
            flex: 1.05,
            minWidth: 180,
            align: 'left',
            headerAlign: 'left',
            renderCell: ({row}) => <TermGaugeCell row={row}/>,
        },
        {
            field: 'quickWinOpportunity',
            headerName: 'Quick win (best term)',
            type: 'number',
            flex: 0.8,
            minWidth: 145,
            renderCell: ({row}) => (
                <span className="pt-wc-cell">
                    {row.quickWinOpportunity > 0 ? formatTableMoney(row.quickWinOpportunity, currency) : '—'}
                </span>
            ),
        },
    ], [currency, termLookup]);

    return <Box className="pt-harmonisation">
        <KpiBoxedValues values={kpis}/>

        <Box className="pt-card">
            <Box className="pt-card-header">
                <Typography variant="h2" className="pt-card-title">
                    Multi-term suppliers ({filtered.length})
                </Typography>
                <TextField
                    className="pt-harm-search"
                    size="small"
                    placeholder="Search supplier or payment term…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchOutlined fontSize="small"/>
                                </InputAdornment>
                            ),
                        },
                    }}
                />
            </Box>
            <CustomDataGrid
                className="base-table-muix auto-row-height-centered"
                rows={filtered}
                columns={columns}
                getRowId={row => row.id}
                autoRowHeight
                disableRowSelectionOnClick
                disableAggregation
                disableRowGrouping
                pagination
                pageSizeOptions={[50, 100, 200]}
                onRowClick={({row}) => onSelect(row)}
                initialState={{
                    pagination: {paginationModel: {pageSize: 50, page: 0}},
                    sorting: {sortModel: [{field: 'quickWinOpportunity', sort: 'desc'}]},
                }}
                sx={{
                    border: 'none',
                    '--rowBorderColor': 'rgba(0,0,0,0.08)',
                    '& .MuiDataGrid-columnHeaderTitle': {fontWeight: 700, whiteSpace: 'normal', lineHeight: 1.2},
                    '& .MuiDataGrid-row': {cursor: 'pointer'},
                }}
            />
            <Typography className="pt-bucket-footnote">
                Showing the multi-term suppliers among the top 250 opportunities. Click a row to drill into every
                term the supplier is paid on and the recommended harmonisation play.
            </Typography>
        </Box>
    </Box>;
});
